import { Injectable, inject } from '@angular/core';
import { AuthenticationActions } from '@navfleet/client/authentication/domain/data-access';
import { AppActions } from '@navfleet/client/core/app';
import { LocalStoragePersistedStorage } from '@navfleet/client/core/local-storage';
import {
    NOTIFICATION_EVENT_FILTERS_STORAGE_KEY,
    NOTIFICATION_STORAGE_PREFIX,
} from '@navfleet/client/fleet/event/domain/models';
import { LicenseFacade } from '@navfleet/client/license/domain/data-access';
import { License } from '@navfleet/client/license/domain/models';
import {
    NotificationEventComment,
    NotificationEventCommentDeletedData,
    NotificationEventData,
    NotificationEventDeactivatedData,
    NotificationEventRemovedData,
} from '@navfleet/client/shared/models/notification';
import { Setting, TenantSettingName } from '@navfleet/client/shared/models/settings';
import { ToasterService } from '@navfleet/client/shared/services/toast';
import { logError } from '@navfleet/client/shared/util/log';
import { TenantFacade } from '@navfleet/client/tenant/domain/data-access';
import { TenantSettingsFacade } from '@navfleet/client/tenant/settings/domain/data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, select } from '@ngrx/store';
import { catchError, filter, map, mergeMap, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { FleetEventApi } from '../api/fleet-event.api';
import {
    FleetEventNotificationSignalRService,
    NotificationMethods,
} from '../services/fleet-event-notification-signal-r.service';
import * as FleetEventActions from './fleet-event.actions';
import * as fromFleetEvent from './fleet-event.reducer';
import * as fleetEventSelectors from './fleet-event.selectors';

@Injectable()
export class FleetEventEffects {
    private readonly actions$ = inject(Actions);
    private readonly licenseFacade = inject(LicenseFacade);
    private readonly store$: Store<fromFleetEvent.State> = inject(Store);
    private readonly fleetEventApi = inject(FleetEventApi);
    private readonly notificationSignalRService = inject(FleetEventNotificationSignalRService);
    private readonly tenantFacade = inject(TenantFacade);
    private readonly tenantSettingsFacade = inject(TenantSettingsFacade);
    private readonly toasterService = inject(ToasterService);

    public readonly initNotificationEventData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.loadAppData),
            this.licenseFacade.hasLicenseFilter(License.Notifications),
            mergeMap(() => [FleetEventActions.loadActiveNotificationEvents()])
        )
    );

    public readonly loadActiveNotificationEventData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FleetEventActions.loadActiveNotificationEvents),
            this.licenseFacade.hasLicenseFilter(License.Notifications),
            switchMap(() =>
                this.fleetEventApi.getActiveNotificationEvents().pipe(
                    map((events) => FleetEventActions.loadActiveNotificationEventsSuccess({ events })),
                    catchError((error) => of(FleetEventActions.loadActiveNotificationEventsFailure({ error })))
                )
            )
        )
    );

    public readonly loadNotificationEvents$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FleetEventActions.loadNotificationEvents),
            this.licenseFacade.hasLicenseFilter(License.Notifications),
            switchMap(({ dateRange, vesselIds }) =>
                this.fleetEventApi.getNotificationEvents(dateRange, vesselIds).pipe(
                    map((events) => FleetEventActions.loadNotificationEventsSuccess({ events })),
                    catchError((error) => of(FleetEventActions.loadNotificationEventsFailure({ error })))
                )
            )
        )
    );

    public readonly acknowledgeNotificationEvent$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FleetEventActions.acknowledgeNotificationEvent),
            this.licenseFacade.hasLicenseFilter(License.Notifications),
            mergeMap(({ id }) =>
                this.fleetEventApi.acknowledgeNotificationEvent(id).pipe(
                    map((event) => FleetEventActions.acknowledgeNotificationEventSuccess({ event })),
                    catchError((error) => of(FleetEventActions.acknowledgeNotificationEventFailure({ error })))
                )
            )
        )
    );

    public readonly unAcknowledgeNotificationEvent$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FleetEventActions.unAcknowledgeNotificationEvent),
            this.licenseFacade.hasLicenseFilter(License.Notifications),
            mergeMap(({ id }) =>
                this.fleetEventApi.unAcknowledgeNotificationEvent(id).pipe(
                    map((event) => FleetEventActions.unAcknowledgeNotificationEventSuccess({ event })),
                    catchError((error) => of(FleetEventActions.unAcknowledgeNotificationEventFailure({ error })))
                )
            )
        )
    );

    public readonly loadNotificationEventComments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FleetEventActions.loadNotificationEventComments),
            this.licenseFacade.hasLicenseFilter(License.Notifications),
            filter(({ id }) => !!id),
            concatLatestFrom(({ id }) => [
                this.store$.pipe(select(fleetEventSelectors.selectNotificationEventById(id))),
                this.store$.pipe(select(fleetEventSelectors.selectActiveEventById(id))),
                this.store$.pipe(select(fleetEventSelectors.selectNotificationEventsCommentsById(id))),
            ]),
            filter(
                ([_, event, activeEvent, comments]) =>
                    event?.numberOfComments !== comments?.length ||
                    (!event && (!comments || activeEvent?.numberOfComments !== comments?.length))
            ),
            switchMap(([{ id }]) =>
                this.fleetEventApi.getNotificationComments(id).pipe(
                    map(({ comments }) => FleetEventActions.loadNotificationEventCommentsSuccess({ id, comments })),
                    catchError((error) => of(FleetEventActions.loadNotificationEventCommentsFailure({ error })))
                )
            )
        )
    );

    public readonly createNotificationEventComments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FleetEventActions.createNotificationEventComment),
            this.licenseFacade.hasLicenseFilter(License.Notifications),
            mergeMap(({ eventId, comment, sendToVessel, sendCC }) =>
                this.fleetEventApi
                    .createNotificationComment({
                        eventId,
                        comment,
                        sendToVessel,
                        sendCC,
                    })
                    .pipe(
                        map((response) =>
                            FleetEventActions.createNotificationEventCommentSuccess({
                                eventId,
                                comment: response,
                            })
                        ),
                        catchError((error) => of(FleetEventActions.createNotificationEventCommentFailure({ error })))
                    )
            )
        )
    );

    public readonly updateNotificationEventComments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FleetEventActions.updateNotificationEventComment),
            this.licenseFacade.hasLicenseFilter(License.Notifications),
            mergeMap(({ comment }) =>
                this.fleetEventApi.updateNotificationComment(comment).pipe(
                    map((response) => FleetEventActions.updateNotificationEventCommentSuccess({ comment: response })),
                    catchError((error) => of(FleetEventActions.updateNotificationEventCommentFailure({ error })))
                )
            )
        )
    );

    public readonly deleteNotificationEventComments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FleetEventActions.deleteNotificationEventComment),
            this.licenseFacade.hasLicenseFilter(License.Notifications),
            mergeMap(({ comment }) =>
                this.fleetEventApi.deleteNotificationComment(comment).pipe(
                    map(() => FleetEventActions.deleteNotificationEventCommentSuccess({ comment })),
                    catchError((error) => of(FleetEventActions.deleteNotificationEventCommentFailure({ error })))
                )
            )
        )
    );

    // * SignalR
    public readonly connectToNotificationSignalR$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthenticationActions.loginSuccess),
                switchMap(() =>
                    this.licenseFacade.hasLicense(License.Notifications).pipe(filter((isLicensed) => !!isLicensed))
                ),
                map(() => this.notificationSignalRService.connect())
            ),
        { dispatch: false }
    );

    public readonly disconnectNotificationSignalR$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AppActions.applicationClosed),
                map(() => this.notificationSignalRService.disconnect())
            ),
        { dispatch: false }
    );

    public readonly signalRNotificationEventActivated$ = createEffect(
        () =>
            this.notificationSignalRService.on$(NotificationMethods.NotificationEventActivated).pipe(
                withLatestFrom(this.tenantFacade.getSelectedTenant()),
                tap(([event, tenant]) => {
                    if (event.tenantId === tenant.id) {
                        this.signalRNotificationEventActivated(event);
                    }
                })
            ),
        { dispatch: false }
    );

    public readonly signalRNotificationEventDeactivated$ = createEffect(
        () =>
            this.notificationSignalRService.on$(NotificationMethods.NotificationEventDeactivated).pipe(
                withLatestFrom(this.tenantFacade.getSelectedTenant()),
                tap(([event, tenant]) => {
                    if (event.tenantId === tenant.id) {
                        this.signalRNotificationEventDeactivated(event);
                    }
                })
            ),
        { dispatch: false }
    );

    public readonly signalRNotificationEventRemoved$ = createEffect(
        () =>
            this.notificationSignalRService.on$(NotificationMethods.NotificationEventRemoved).pipe(
                withLatestFrom(this.tenantFacade.getSelectedTenant()),
                tap(([event, tenant]) => {
                    if (event.tenantId === tenant.id) {
                        this.signalRNotificationEventRemoved(event);
                    }
                })
            ),
        { dispatch: false }
    );

    public readonly signalRNotificationEventAcknowledged$ = createEffect(
        () =>
            this.notificationSignalRService.on$(NotificationMethods.NotificationEventAcknowledged).pipe(
                withLatestFrom(this.tenantFacade.getSelectedTenant()),
                tap(([event, tenant]) => {
                    if (event.tenantId === tenant.id) {
                        this.signalRNotificationEventAcknowledged(event);
                    }
                })
            ),
        { dispatch: false }
    );

    public readonly signalRNotificationEventUnAcknowledged$ = createEffect(
        () =>
            this.notificationSignalRService.on$(NotificationMethods.NotificationEventUnAcknowledged).pipe(
                withLatestFrom(this.tenantFacade.getSelectedTenant()),
                tap(([event, tenant]) => {
                    if (event.tenantId === tenant.id) {
                        this.signalRNotificationEventUnAcknowledged(event);
                    }
                })
            ),
        { dispatch: false }
    );

    public readonly signalRNotificationEventCommentAdded$ = createEffect(
        () =>
            this.notificationSignalRService.on$(NotificationMethods.NotificationEventCommentAdded).pipe(tap((event) => this.signalRNotificationEventCommentAdded(event))),
        { dispatch: false }
    );

    public readonly signalRNotificationEventCommentUpdated$ = createEffect(
        () =>
            this.notificationSignalRService.on$(NotificationMethods.NotificationEventCommentUpdated).pipe(tap((event) => this.signalRNotificationEventCommentUpdated(event))),
        { dispatch: false }
    );

    public readonly signalRNotificationEventCommentDeleted$ = createEffect(
        () =>
            this.notificationSignalRService.on$(NotificationMethods.NotificationEventCommentDeleted).pipe(tap((event) => this.signalRNotificationEventCommentDeleted(event))),
        { dispatch: false }
    );

    public readonly resetNotificationFiltersOnLogin$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthenticationActions.loginTenant),
                tap(() => this.resetNotificationFilters())
            ),
        { dispatch: false }
    );

    public readonly saveOnToggleOrUpdateAcknowledge$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(FleetEventActions.allowNotificationUnAcknowledge),
                withLatestFrom(this.tenantSettingsFacade.getTenantSetting('AllowNotificationEventUnAcknowledge')),
                filter(([, setting]) => !!setting && setting.canSet),
                map(([state, setting]) => {
                    let value = '';
                    try {
                        value = JSON.stringify(state.value) || JSON.stringify({});
                    } catch (error) {
                        logError('Error converting AllowNotificationEventUnAcknowledge value to string', state, error);
                        throw new Error('');
                    }

                    return { ...setting, value } as Setting<TenantSettingName>;
                }),
                tap((setting) => this.tenantSettingsFacade.updateTenantSetting(setting)),
                catchError(() =>
                    of(this.toasterService.error('FLEET.EVENT.DETAILS.ERROR.UNABLE_TO_SAVE_UNACKNOWLEDGEMENT_SETTING'))
                )
            ),
        { dispatch: false }
    );

    private signalRNotificationEventActivated(event: NotificationEventData) {
        this.store$.dispatch(FleetEventActions.signalRNotificationEventActivated({ event }));
    }

    private signalRNotificationEventDeactivated(event: NotificationEventDeactivatedData) {
        this.store$.dispatch(FleetEventActions.signalRNotificationEventDeactivated({ event }));
    }

    private signalRNotificationEventRemoved(data: NotificationEventRemovedData) {
        this.store$.dispatch(FleetEventActions.signalRNotificationEventRemoved({ data }));
    }

    private signalRNotificationEventAcknowledged(event: NotificationEventData) {
        this.store$.dispatch(FleetEventActions.signalRNotificationEventAcknowledged({ event }));
    }

    private signalRNotificationEventUnAcknowledged(event: NotificationEventData) {
        this.store$.dispatch(FleetEventActions.signalRNotificationEventUnAcknowledged({ event }));
    }

    private signalRNotificationEventCommentAdded(comment: NotificationEventComment) {
        this.store$.dispatch(
            FleetEventActions.createNotificationEventCommentSuccess({
                eventId: comment.notificationEventId,
                comment,
            })
        );
    }

    private signalRNotificationEventCommentUpdated(comment: NotificationEventComment) {
        this.store$.dispatch(FleetEventActions.updateNotificationEventCommentSuccess({ comment }));
    }

    private signalRNotificationEventCommentDeleted(data: NotificationEventCommentDeletedData) {
        this.store$.dispatch(FleetEventActions.signalRNotificationEventCommentDeleted({ data }));
    }

    private resetNotificationFilters(): void {
        const storage = new LocalStoragePersistedStorage(NOTIFICATION_STORAGE_PREFIX);
        storage.clear(NOTIFICATION_EVENT_FILTERS_STORAGE_KEY);
    }
}
